import type { GeneratedPlayResponse } from '@playbooks/core/src/services/BackendClient'

/**
 * Compact Action Card update produced by the AI Teammate `update_action_card` tool call.
 */
export interface CompactTeammateUpdate {
  qId: number
  ans?: string
  ansIds?: number[]
  conf: number
}

/**
 * Convert a compact AI Teammate update to a GeneratedPlayResponse with real Action Card IDs.
 * AI refers to questions and answer options by the short IDs assigned to the sections sent to AI
 * (see `shortenTeammateIds`), so both are resolved back using the ID map.
 * @param update Compact update from the `update_action_card` tool call: short question ID (`qId`),
 *   text answer (`ans`), short IDs of selected answer options (`ansIds`) and confidence (`conf`).
 * @param actionCardIdMap Map of short IDs used by AI to the real Action Card IDs.
 * @returns The update with real Action Card IDs, or null if the question ID is unknown.
 *   Unknown answer option IDs are filtered out.
 */
export function resolveTeammateUpdate(
  update: CompactTeammateUpdate,
  actionCardIdMap: Record<string, number>,
): GeneratedPlayResponse | null {
  const questionId = actionCardIdMap[update.qId]
  if (questionId === undefined) {
    console.warn('Ignoring AI Teammate update for an unknown question ID', update)
    return null
  }

  return {
    questionId,
    answer: update.ans,
    answerIds: update.ansIds?.map(id => actionCardIdMap[id]).filter(id => id !== undefined),
    confidence: update.conf,
  }
}
